// Recipe storage uses grams, fractional lye concentration and matching aliases
// while older screens and exports transition to schema version 2.
const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const present = value => value !== null && value !== undefined && value !== '';
const firstNumber = (values, fallback = 0) => {
  const value = values.find(item => present(item) && Number.isFinite(Number(item)));
  return value === undefined ? fallback : Number(value);
};
const asArray = (value, fallback) => Array.isArray(value) ? value : present(value) ? [value] : fallback;

export const concentrationFraction = (value, fallback = 0.33) => {
  const concentration = present(value) ? number(value, fallback) : fallback;
  return concentration > 1 ? concentration / 100 : concentration;
};

export const weightToGrams = (amount, unit = 'g', densityGPerMl) => {
  const quantity = Number(amount);
  if (!Number.isFinite(quantity)) return NaN;
  const units = { g: 1, gram: 1, grams: 1, kg: 1000, oz: 28.349523125, lb: 453.59237 };
  const normalizedUnit = String(unit || 'g').toLowerCase();
  if (units[normalizedUnit]) return quantity * units[normalizedUnit];
  if (['ml', 'l', 'liter', 'litre'].includes(normalizedUnit) && Number(densityGPerMl) > 0) {
    return quantity * Number(densityGPerMl) * (normalizedUnit === 'ml' ? 1 : 1000);
  }
  return NaN;
};
export const gramsToWeight = (grams, unit = 'g') => {
  const factor = weightToGrams(1, unit);
  return Number.isFinite(factor) && factor > 0 ? number(grams) / factor : number(grams);
};
export const additiveGrams = additive => {
  if (present(additive.grams) && Number.isFinite(Number(additive.grams))) return Number(additive.grams);
  return weightToGrams(additive.amount, additive.unit || 'g', additive.densityGPerMl);
};

export const getRecipeLye = (recipe = {}) => firstNumber([
  recipe.lyeRequired, recipe.adjustedTotalNaoh, recipe.naoh,
  present(recipe.naohWeight) || present(recipe.kohWeight) ? number(recipe.naohWeight) + number(recipe.kohWeight) : undefined,
  recipe.alkaliWeights ? number(recipe.alkaliWeights.NaOH) + number(recipe.alkaliWeights.KOH) : undefined,
]);

export const getRecipeBatchMass = (recipe = {}) => {
  const lye = getRecipeLye(recipe);
  const water = number(recipe.waterWeight ?? recipe.water);
  // Melt-and-pour has no lye or water; its mass is the base plus additives.
  if (number(recipe.totalOilWeight) > 0 && (lye > 0 || water > 0 || recipe.recipeType === 'melt_pour')) {
    const additives = (recipe.additives || []).reduce((sum, item) => sum + Math.max(0, number(additiveGrams(item))), 0);
    return number(recipe.totalOilWeight) + lye + water + additives;
  }
  return firstNumber([recipe.soapWeight, recipe.computedBatchMass, recipe.batchMass, recipe.batchSize, recipe.totalOilWeight]);
};

export function normalizeRecipe(input = {}) {
  const sourceOils = Array.isArray(input.items) ? input.items : Array.isArray(input.oils) ? input.oils :
    Array.isArray(input.oils?.oils) ? input.oils.oils : [];
  const totalOilWeight = firstNumber([input.totalOilWeight], 1000);
  const oils = sourceOils.map(oil => {
    const pct = firstNumber([oil.pct, oil.percent]);
    return { ...oil, pct, percent: pct, weight: totalOilWeight * pct / 100 };
  });
  const lyeRequired = getRecipeLye(input);
  const waterWeight = firstNumber([input.waterWeight, input.water]);
  const additives = (Array.isArray(input.additives) ? input.additives : []).map(item => ({ ...item }));
  const soapWeight = getRecipeBatchMass({ ...input, totalOilWeight, lyeRequired, waterWeight, additives });
  const barWeight = firstNumber([input.barWeight], 100);
  const bars = firstNumber([input.bars], Math.max(1, Math.floor(soapWeight / Math.max(1, barWeight))));
  const legacyCosts = input.costs || {};
  const cost = {
    ...input.cost,
    material: firstNumber([input.cost?.material, legacyCosts.material]),
    working: firstNumber([input.cost?.working, legacyCosts.labor]),
    packaging: firstNumber([input.cost?.packaging, legacyCosts.packaging]),
  };
  const totalCost = firstNumber([input.totals?.totalCost, legacyCosts.total], cost.material + cost.working + cost.packaging);
  const totals = {
    ...input.totals,
    baseCost: firstNumber([input.totals?.baseCost], cost.material),
    workingCost: firstNumber([input.totals?.workingCost], cost.working),
    packagingCost: firstNumber([input.totals?.packagingCost], cost.packaging),
    totalCost, perBar: firstNumber([input.totals?.perBar, legacyCosts.perBar], totalCost / Math.max(1, bars)), bars,
  };
  const dualLye = Boolean(input.dualLye || input.lyeType === 'dual');
  const lyeType = input.lyeType === 'KOH' ? 'KOH' : 'NaOH';
  const alkaliWeights = input.alkaliWeights || (dualLye ? {} : { NaOH: lyeType === 'NaOH' ? lyeRequired : 0, KOH: lyeType === 'KOH' ? lyeRequired : 0 });
  return {
    ...input, schemaVersion: 2, massUnit: 'g', items: oils, oils, additives,
    totalOilWeight, barWeight, bars, lyeType, dualLye, alkaliWeights,
    lyePurity: firstNumber([input.lyePurity], 100),
    kohPurity: firstNumber([input.kohPurity, input.lyePurity], 100),
    superfat: firstNumber([input.superfat], 5),
    kohRatio: firstNumber([input.kohRatio], 50),
    unit: ['g', 'oz', 'lb'].includes(input.unit) ? input.unit : 'g',
    lyeConcentration: concentrationFraction(input.lyeConcentration),
    actualLyeConcentration: lyeRequired > 0 && waterWeight > 0 ? lyeRequired / (lyeRequired + waterWeight) : null,
    waterMethod: input.waterMethod || (present(input.lyeConcentration) ? 'concentration' : 'percent'),
    waterLyeRatio: firstNumber([input.waterLyeRatio], 2),
    waterPercent: firstNumber([input.waterPercent], 38),
    waterDiscount: firstNumber([input.waterDiscount], 0),
    waterWeight, lyeRequired, adjustedTotalNaoh: lyeRequired,
    soapWeight, computedBatchMass: soapWeight, batchSize: soapWeight, batchMass: soapWeight,
    use: asArray(input.use, ['body']), skinType: asArray(input.skinType, ['all']),
    cost, totals,
    // A legacy unchecked box never published anything. Do not turn that old
    // default into fresh consent to publish during schema migration.
    makePrivate: input.schemaVersion === 2 || input.isPublic === true ? input.makePrivate !== false : true,
    isPublic: input.isPublic === true,
    publicRecipeId: input.publicRecipeId || null,
    createTemplate: Boolean(input.createTemplate ?? input.isTemplate),
    laborCostPerBar: firstNumber([input.laborCostPerBar], cost.working / Math.max(1, bars)),
    packagingCostPerBar: firstNumber([input.packagingCostPerBar], cost.packaging / Math.max(1, bars)),
    markupPercent: firstNumber([input.markupPercent, input.pricing?.markup], 120),
  };
}

export function scaleRecipe(input, multiplier) {
  const factor = Number(multiplier);
  if (!Number.isFinite(factor) || factor <= 0) throw new Error('Scale multiplier must be greater than zero.');
  const recipe = normalizeRecipe(input);
  const scaleFields = (object, fields) => Object.fromEntries(Object.entries(object || {}).map(([key, value]) =>
    [key, fields.includes(key) && present(value) ? number(value) * factor : value]));
  const additives = recipe.additives.map(item => scaleFields(item, ['amount', 'grams']));
  const bars = Math.max(1, Math.floor(recipe.bars * factor));
  const totalCost = recipe.totals.totalCost * factor;
  const oils = recipe.oils.map(item => scaleFields(item, ['weight', 'grams', 'scaledWeight', 'naohForOil', 'kohForOil', 'adjustedNaoh', 'lineCost']));
  return normalizeRecipe({
    ...recipe, totalOilWeight: recipe.totalOilWeight * factor, additives,
    items: oils, oils,
    perOilDetails: (recipe.perOilDetails || recipe.oils).map(item => scaleFields(item, ['weight', 'grams', 'naohForOil', 'kohForOil', 'adjustedNaoh', 'lineCost'])),
    lyeRequired: recipe.lyeRequired * factor, waterWeight: recipe.waterWeight * factor,
    alkaliWeights: Object.fromEntries(Object.entries(recipe.alkaliWeights).map(([key, value]) => [key, number(value) * factor])),
    naohWeight: number(recipe.alkaliWeights.NaOH) * factor, kohWeight: number(recipe.alkaliWeights.KOH) * factor,
    bars, cost: scaleFields(recipe.cost, ['material', 'working', 'packaging', 'baseCost', 'workingCost', 'packagingCost', 'totalCost']),
    costs: scaleFields(recipe.costs, ['oils', 'additives', 'lye', 'material', 'packaging', 'labor', 'total']),
    totals: { ...scaleFields(recipe.totals, ['baseCost', 'workingCost', 'packagingCost', 'totalCost']), totalCost, bars, perBar: totalCost / bars },
  });
}
